import type { Stores } from '../../../../../../util/store'
import { useCallback, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import styles from './manufacturer-wizard.module.scss'
import { Button, Chip } from '@mui/material'
import { ChevronLeft } from '@mui/icons-material'

interface WOModelManufacturerAutoMatchProps {
    stores: Stores
}
export const WOManufacturerMatchWizard = observer(
    ({ stores }: WOModelManufacturerAutoMatchProps) => {
        const [currentIndex, setCurrentIndex] = useState(0)
        const [newAlias, setNewAlias] = useState('')
        const [newAliasManufacturerId, setNewAliasManufacturerId] = useState<
            string | undefined
        >(undefined)
        const { manufacturerMatches, loading, createManufacturerAlias } =
            stores.manufacturerModelMatch

        const currentManufacturer = useMemo(
            () => manufacturerMatches[currentIndex],
            [currentIndex, manufacturerMatches],
        )
        const handleSkipOnClick = useCallback(() => {
            setNewAlias('')
            setNewAliasManufacturerId(undefined)
            setCurrentIndex(currentIndex + 1)
        }, [currentIndex])
        const handleMatchOnClick = useCallback(() => {
            if (
                newAliasManufacturerId &&
                currentManufacturer?.existing_manufacturer_aliases[0].id
            ) {
                createManufacturerAlias(
                    currentManufacturer?.existing_manufacturer_aliases[0].id,
                    newAliasManufacturerId,
                )
                    .then(() => {
                        handleSkipOnClick()
                    })
                    .catch((e) => {
                        console.error('Failed to match', e)
                    })
            } else {
                console.error(
                    'Failed to match, No manufacturer alias found, so update does not work',
                )
            }
        }, [
            currentManufacturer?.existing_manufacturer_aliases,
            handleSkipOnClick,
            createManufacturerAlias,
            newAliasManufacturerId,
        ])

        const existingAliasSuggestions = () => {
            const existingAliases =
                currentManufacturer?.existing_manufacturer_aliases
            if (!existingAliases || existingAliases.length === 0) return null
            return (
                <div className={styles.suggestionWrapper}>
                    <p className={styles.smallHeader}>Existing Aliases: </p>
                    {existingAliases.map((alias) => {
                        return (
                            <Chip
                                onClick={() => {
                                    setNewAlias(
                                        alias.product_manufacturer?.name ?? '',
                                    )
                                    setNewAliasManufacturerId(
                                        alias.product_manufacturer?.id,
                                    )
                                }}
                                className={styles.suggestionOption}
                                label={
                                    alias.product_manufacturer?.name ??
                                    `NULL (alias id: ${alias.id})`
                                }
                            />
                        )
                    })}
                </div>
            )
        }

        const regexSuggestions = useCallback(() => {
            const regexMatches = currentManufacturer?.regex_manufacturer_matches
            if (!regexMatches || regexMatches.length === 0) return null
            return (
                <div className={styles.suggestionWrapper}>
                    <p className={styles.smallHeader}>Regex Matches: </p>
                    {regexMatches.map((regexMatch) => {
                        return (
                            <Chip
                                onClick={() => {
                                    setNewAlias(regexMatch.name)
                                    setNewAliasManufacturerId(regexMatch.id)
                                }}
                                className={styles.suggestionOption}
                                label={regexMatch.name}
                            />
                        )
                    })}
                </div>
            )
        }, [currentManufacturer?.regex_manufacturer_matches])
        if (!currentManufacturer) return <div>Loading...</div>
        return (
            <div data-testid="org-wo-mm-manufacturer-wizard-page" className={styles.container}>
                <div className={styles.summaryWrapper}>
                    {currentIndex > 0 && (
                        <div className={styles.summaryItem}>
                            <p className={styles.smallHeader}>
                                <Button
                                    className={styles.button}
                                    onClick={() =>
                                        setCurrentIndex(currentIndex - 1)
                                    }>
                                    <ChevronLeft />
                                </Button>
                            </p>
                        </div>
                    )}
                    <div className={styles.summaryItem}>
                        <p className={styles.smallHeader}>
                            {`Input Manufacturer Name: (#${currentIndex + 1}/${manufacturerMatches.length})`}
                        </p>
                        <div className={styles.header}>
                            <span>{currentManufacturer.name}</span>
                        </div>
                    </div>
                    <div className={styles.summaryItem}>
                        <p className={styles.smallHeader}>
                            Work Order Segment Count:
                        </p>
                        <div className={styles.header}>
                            {currentManufacturer.work_order_segments_count}
                        </div>
                    </div>
                    <div className={styles.inputModelWrapper}>
                        <p
                            className={
                                styles.smallHeader
                            }>{`Input Model Names (${currentManufacturer.model_names.length} input models associated):`}</p>
                        <div className={styles.inputModelList}>
                            {currentManufacturer.model_names.join(', ')}
                        </div>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(
                                    currentManufacturer.model_names.join(', '),
                                )
                            }}>
                            Copy to Clipboard
                        </button>
                    </div>
                </div>
                <div className={styles.suggestionContainer}>
                    {existingAliasSuggestions()}
                    {regexSuggestions()}
                </div>
                <div className={styles.footer}>
                    <div className={styles.footerInputWrapper}>
                        <p className={styles.smallHeader}>
                            Matching Manufacturer Name:
                        </p>
                        <input
                            className={styles.footerInput}
                            type="text"
                            value={newAlias}
                            onChange={(e) => {
                                setNewAlias(e.target.value)
                            }}
                        />
                        <p className={styles.smallHeader}>
                            {`Corresponding Manufacturer ID: ${newAliasManufacturerId ?? 'not found'}`}
                        </p>
                    </div>
                    <Button
                        disabled={
                            currentIndex === manufacturerMatches.length - 1 &&
                            loading
                        }
                        className={styles.footerButton}
                        onClick={handleSkipOnClick}>
                        Skip
                    </Button>
                    <Button
                        disabled={loading || !newAliasManufacturerId}
                        className={styles.footerButton}
                        onClick={handleMatchOnClick}>
                        {loading ? 'Matching...' : 'Match'}
                    </Button>
                </div>
            </div>
        )
    },
)
